<template>
  <div class="analytics-container">
    <!-- 切换直播模态框 -->
    <input class="peer hidden" id="switch-stream-modal" type="checkbox" />
    
    <div class="flex h-screen w-full has-[:checked]:blur-sm transition-all duration-300">
      <!-- 注意：左栏导航栏已经在App.vue中实现，所以这里只包含主要内容 -->
      
      <main class="flex-1 flex flex-col gap-6 p-6 overflow-y-auto bg-background-light dark:bg-background-dark font-display text-text-main dark:text-text-main-dark">
        <!-- 主播信息头部卡片 -->
        <div class="w-full bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div class="flex flex-col gap-1 w-full lg:w-3/4">
            <!-- 加载状态 -->
            <div v-if="isLoading" class="flex items-center gap-3 mb-1">
              <div class="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
              <div class="h-6 w-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
            </div>
            
            <!-- 数据加载完成后的显示 -->
                <div v-else-if="creatorDetail">
                  <div class="flex items-center gap-3 mb-1">
                    <h2 class="text-2xl font-bold text-primary">{{ creatorDetail.creatorNickname || creatorDetail.socialHandle }}</h2>
                    <span class="px-2 py-0.5 rounded bg-black text-white border border-white/10 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                      <span class="material-symbols-outlined text-[12px]">live_tv</span>
                      {{ creatorDetail.platform || $t('analyticsPage.defaultValues.platform') }}
                    </span>
                  </div>
                  <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                    <a
                      v-if="creatorDetail.socialHandle"
                      :href="`https://www.tiktok.com/@${creatorDetail.socialHandle}`"
                      target="_blank"
                      class="font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors underline decoration-slate-300 dark:decoration-slate-600 hover:decoration-slate-500 dark:hover:decoration-slate-400"
                    >
                      @{{ creatorDetail.socialHandle }}
                    </a>
                    <span v-else class="font-semibold text-slate-500 dark:text-slate-400">@{{ $t('common.notSet') }}</span>
                    <div class="h-1 w-1 bg-slate-300 dark:bg-slate-600 rounded-full"></div>
                    <span class="text-slate-500 dark:text-slate-400">{{ $t('app.followers') }}: <span class="text-slate-900 dark:text-white font-bold">{{ formatFollowerCount(creatorDetail.creatorFollowerCount) }}</span></span>
                    <div class="h-1 w-1 bg-slate-300 dark:bg-slate-600 rounded-full"></div>
                    <span class="text-slate-500 dark:text-slate-400">{{ $t('app.videos') }}: <span class="text-slate-900 dark:text-white font-bold">{{ creatorDetail.videoCount || $t('analyticsPage.defaultValues.zero') }}</span></span>
                    <div class="h-1 w-1 bg-slate-300 dark:bg-slate-600 rounded-full"></div>
                    <span class="text-slate-500 dark:text-slate-400">{{ $t('common.status') }}: <span class="text-slate-900 dark:text-white font-bold">{{ getBindingStatusText(creatorDetail.bindingStatus) }}</span></span>
                  </div>
                  <div class="flex flex-wrap items-center gap-x-4 gap-y-2 mt-1 text-sm text-slate-500 dark:text-slate-500">
                    <div class="flex items-center gap-2">
                      <span class="dark:text-slate-400">{{ $t('analyticsPage.trackingSince') }}: <span class="font-medium text-slate-700 dark:text-slate-200">{{ formatDate(creatorDetail.createTime) }}</span></span>
                    </div>
                    <div class="h-4 w-px bg-slate-300 dark:bg-slate-700 hidden sm:block"></div>
                    <div class="flex gap-2">
                      <span v-if="creatorDetail.isLive" class="px-2 py-0.5 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800/50 rounded text-[10px] font-semibold uppercase tracking-wide">{{ $t('analyticsPage.statusTags.liveNow') }}</span>
                      <span v-if="creatorDetail.creatorIsVerified" class="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50 rounded text-[10px] font-semibold uppercase tracking-wide">{{ $t('analyticsPage.statusTags.verified') }}</span>
                      <span v-if="creatorDetail.isPrimary" class="px-2 py-0.5 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800/50 rounded text-[10px] font-semibold uppercase tracking-wide">{{ $t('analyticsPage.statusTags.primary') }}</span>
                      <span v-if="creatorDetail.verificationStatus === 1" class="px-2 py-0.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800/50 rounded text-[10px] font-semibold uppercase tracking-wide">{{ $t('analyticsPage.statusTags.verificationStatus') }}</span>
                    </div>
                  </div>
                </div>
            
            <!-- 数据加载失败或无数据 -->
            <div v-else>
              <div class="flex items-center gap-3 mb-1">
                <h2 class="text-2xl font-bold text-primary">{{ $t('analyticsPage.unboundStreamer') }}</h2>
              </div>
              <div class="text-slate-500 dark:text-slate-400 text-sm">
                {{ $t('analyticsPage.bindFirst') }}
              </div>
            </div>
            
            <!-- 错误信息 -->
            <div v-if="errorMessage" class="mt-2 text-sm text-red-500">
              {{ errorMessage }}
            </div>
          </div>
          <div class="flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto self-end lg:self-center">
            <button @click="viewFullReport" class="flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white text-sm font-bold rounded-lg shadow-lg shadow-primary/20 transition-colors w-full sm:w-auto">
              <span class="material-symbols-outlined text-sm">description</span>
              {{ $t('analyticsPage.viewFullReport') }}
            </button>
          </div>
        </div>
        
        <!-- 主要内容网格 -->
        <div class="grid grid-cols-12 gap-6">
          <!-- 左侧概览区域 -->
          <div class="col-span-12 lg:col-span-5 flex flex-col gap-6">
            <!-- 概览标题和时间选择 -->
            <div class="flex justify-between items-center px-1">
              <h3 class="text-slate-900 dark:text-white text-lg font-bold">{{ $t('analyticsPage.overview') }}</h3>
              <div class="flex bg-slate-50 dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700 items-center">
                <span class="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider mr-1 hidden xl:block">{{ $t('analyticsPage.period') }}</span>
                <button 
                  @click="selectPeriod('30d')"
                  :class="selectedPeriod === '30d' ? 'px-3 py-1 text-xs font-bold text-slate-900 dark:text-white bg-white dark:bg-primary shadow-sm border border-slate-100 dark:border-primary-hover rounded-md' : 'px-3 py-1 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-700 rounded-md transition-all'"
                >
                  30d
                </button>
                <button 
                  @click="selectPeriod('60d')"
                  :class="selectedPeriod === '60d' ? 'px-3 py-1 text-xs font-bold text-slate-900 dark:text-white bg-white dark:bg-primary shadow-sm border border-slate-100 dark:border-primary-hover rounded-md' : 'px-3 py-1 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-700 rounded-md transition-all'"
                >
                  60d
                </button>
                <button 
                  @click="selectPeriod('90d')"
                  :class="selectedPeriod === '90d' ? 'px-3 py-1 text-xs font-bold text-slate-900 dark:text-white bg-white dark:bg-primary shadow-sm border border-slate-100 dark:border-primary-hover rounded-md' : 'px-3 py-1 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-700 rounded-md transition-all'"
                >
                  90d
                </button>
                <div class="w-px h-4 bg-slate-300 dark:bg-slate-600 mx-1"></div>
                <div class="relative">
                  <button @click="toggleMonthPicker" class="flex items-center gap-1 px-3 py-1 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                    <span>{{ getMonthButtonText() }}</span>
                    <span class="material-symbols-outlined text-[14px]">arrow_drop_down</span>
                  </button>
                  
                  <!-- 月份选择器 -->
                  <div v-if="showMonthPicker" class="absolute top-full right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 w-48">
                    <div class="p-3">
                      <!-- 年份和月份导航 -->
              <div class="flex items-center justify-between mb-3">
                <button @click="prevYear" class="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                  <span class="material-symbols-outlined text-sm">chevron_left</span>
                </button>
                <span class="text-sm font-semibold text-slate-900 dark:text-white">{{ currentYear }}{{ $t('analyticsPage.year') }}</span>
                <button @click="nextYear" class="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                  <span class="material-symbols-outlined text-sm">chevron_right</span>
                </button>
              </div>
                      
                      <!-- 月份网格 -->
                      <div class="grid grid-cols-3 gap-2">
                        <button 
                          v-for="month in 12" 
                          :key="month"
                          @click="selectMonthFromPicker(month - 1)"
                          :class="[
                            'py-2 text-xs font-medium rounded transition-colors',
                            isSelectedMonth(month - 1) 
                              ? 'bg-primary text-white' 
                              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                          ]"
                        >
                          {{ getMonthName(month - 1) }}
                        </button>
                      </div>
                      
                      <!-- 快速操作 -->
                      <div class="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                        <button 
                          @click="selectCurrentMonth"
                          class="w-full py-1.5 text-xs font-medium text-primary hover:bg-primary/10 rounded transition-colors"
                        >
                          {{ $t('analyticsPage.monthPicker.currentMonth') }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 加载状态 -->
            <div v-if="isOverviewLoading" class="grid grid-cols-2 gap-4">
              <div v-for="n in 8" :key="n" class="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm">
                <div class="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mb-2"></div>
                <div class="h-32 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div>
              </div>
            </div>
            
            <!-- 错误信息 -->
            <div v-if="overviewError" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl p-4">
              <p class="text-red-600 dark:text-red-400 text-sm">{{ overviewError }}</p>
            </div>
            
            <!-- KPI卡片网格 -->
            <div v-if="!isOverviewLoading && !overviewError" class="grid grid-cols-2 gap-4">
              <!-- KPI卡片 1: 新开播场次 -->
              <div class="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm flex flex-col justify-between gap-6 relative overflow-hidden group hover:border-primary/30 dark:hover:border-primary/50 transition-all">
                <div class="flex justify-between items-start z-10">
                  <div class="p-2 rounded-full bg-primary/10 text-primary dark:border dark:border-primary/20">
                    <span class="material-symbols-outlined text-[20px]">videocam</span>
                  </div>
                  <div class="text-right">
                    <p class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight">{{ $t('analyticsPage.sessionCount') }}</p>
                    <p class="text-2xl font-bold text-primary">{{ getMetricValue('sessionCount') || '0' }}<span class="text-sm text-slate-500 dark:text-slate-400 ml-1">{{ $t('analyticsPage.sessionUnit') }}</span></p>
                    <p class="text-[10px] text-slate-400 dark:text-slate-500 font-medium mt-0.5">{{ $t('analyticsPage.hoverDesc.sessionCount') }}</p>
                  </div>
                </div>
                <div class="h-12 w-full mb-0 z-0 opacity-70 group-hover:opacity-100 transition-opacity mt-2">
                  <div id="chart-1" class="h-full w-full"></div>
                </div>
              </div>
              
              <!-- KPI卡片 2: 直播时长 -->
              <div class="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm flex flex-col justify-between gap-6 relative overflow-hidden group hover:border-primary/30 dark:hover:border-primary/50 transition-all">
                <div class="flex justify-between items-start z-10">
                  <div class="p-2 rounded-full bg-primary/10 text-primary dark:border dark:border-primary/20">
                    <span class="material-symbols-outlined text-[20px]">schedule</span>
                  </div>
                  <div class="text-right">
                    <p class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight">{{ $t('analyticsPage.totalDuration') }}</p>
                    <p class="text-2xl font-bold text-primary">{{ formatDuration(getMetricValue('duration')) || '0' }}</p>
                    <p class="text-[10px] text-slate-400 dark:text-slate-500 font-medium mt-0.5">{{ $t('analyticsPage.hoverDesc.duration') }}</p>
                  </div>
                </div>
                <div class="h-12 w-full mb-0 z-0 opacity-70 group-hover:opacity-100 transition-opacity mt-2">
                  <div id="chart-2" class="h-full w-full"></div>
                </div>
              </div>
              
              <!-- KPI卡片 3: 平均在线用户 -->
              <div class="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm flex flex-col justify-between gap-6 relative overflow-hidden group hover:border-primary/30 dark:hover:border-primary/50 transition-all">
                <div class="flex justify-between items-start z-10">
                  <div class="p-2 rounded-full bg-primary/10 text-primary dark:border dark:border-primary/20">
                    <span class="material-symbols-outlined text-[20px]">group</span>
                  </div>
                  <div class="text-right">
                    <p class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight">{{ $t('analyticsPage.avgOnline') }}</p>
                    <p class="text-2xl font-bold text-primary">{{ getMetricValue('avgOnlineUsers') || '0' }}<span class="text-sm text-slate-500 dark:text-slate-400 ml-1">{{ $t('analyticsPage.personUnit') }}</span></p>
                    <p class="text-[10px] text-slate-400 dark:text-slate-500 font-medium mt-0.5">{{ $t('analyticsPage.hoverDesc.avgOnlineUsers') }}</p>
                  </div>
                </div>
                <div class="h-12 w-full mb-0 z-0 opacity-70 group-hover:opacity-100 transition-opacity mt-2">
                  <div id="chart-3" class="h-full w-full"></div>
                </div>
              </div>
              
              <!-- KPI卡片 4: 互动密度 -->
              <div class="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm flex flex-col justify-between gap-6 relative overflow-hidden group hover:border-primary/30 dark:hover:border-primary/50 transition-all">
                <div class="flex justify-between items-start z-10">
                  <div class="p-2 rounded-full bg-primary/10 text-primary dark:border dark:border-primary/20">
                    <span class="material-symbols-outlined text-[20px]">chat</span>
                  </div>
                  <div class="text-right">
                    <p class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight">{{ $t('analyticsPage.interactionIndex') }}</p>
                    <p class="text-2xl font-bold text-primary">{{ getMetricValue('interactionDensity') || '0' }}</p>
                    <p class="text-[10px] text-slate-400 dark:text-slate-500 font-medium mt-0.5">{{ $t('analyticsPage.hoverDesc.interactionDensity') }}</p>
                  </div>
                </div>
                <div class="h-12 w-full mb-0 z-0 opacity-70 group-hover:opacity-100 transition-opacity mt-2">
                  <div id="chart-4" class="h-full w-full"></div>
                </div>
              </div>
              
              <!-- KPI卡片 5: 营收统计 -->
              <div class="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm flex flex-col justify-between gap-6 relative overflow-hidden group hover:border-primary/30 dark:hover:border-primary/50 transition-all">
                <div class="flex justify-between items-start z-10">
                  <div class="p-2 rounded-full bg-primary/10 text-primary dark:border dark:border-primary/20">
                    <span class="material-symbols-outlined text-[20px]">payments</span>
                  </div>
                  <div class="text-right">
                    <p class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight">{{ $t('analyticsPage.revenuePerMinute') }}</p>
                    <p class="text-2xl font-bold text-primary">${{ getMetricValue('revenue') || '0' }}</p>
                    <p class="text-[10px] text-slate-400 dark:text-slate-500 font-medium mt-0.5">{{ $t('analyticsPage.hoverDesc.revenue') }}</p>
                  </div>
                </div>
                <div class="h-12 w-full mb-0 z-0 opacity-70 group-hover:opacity-100 transition-opacity mt-2">
                  <div id="chart-5" class="h-full w-full"></div>
                </div>
              </div>
              
              <!-- KPI卡片 6: 打赏用户 -->
              <div class="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm flex flex-col justify-between gap-6 relative overflow-hidden group hover:border-primary/30 dark:hover:border-primary/50 transition-all">
                <div class="flex justify-between items-start z-10">
                  <div class="p-2 rounded-full bg-primary/10 text-primary dark:border dark:border-primary/20">
                    <span class="material-symbols-outlined text-[20px]">favorite</span>
                  </div>
                  <div class="text-right">
                    <p class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight">{{ $t('analyticsPage.rewardUsers') }}</p>
                    <p class="text-2xl font-bold text-primary">{{ getMetricValue('rewardUsers') || '0' }}<span class="text-sm text-slate-500 dark:text-slate-400 ml-1">{{ $t('analyticsPage.personUnit') }}</span></p>
                    <p class="text-[10px] text-slate-400 dark:text-slate-500 font-medium mt-0.5">{{ $t('analyticsPage.hoverDesc.rewardUsers') }}</p>
                  </div>
                </div>
                <div class="h-12 w-full mb-0 z-0 opacity-70 group-hover:opacity-100 transition-opacity mt-2">
                  <div id="chart-6" class="h-full w-full"></div>
                </div>
              </div>
              
              <!-- KPI卡片 7: 平均礼物价值 -->
              <div class="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm flex flex-col justify-between gap-6 relative overflow-hidden group hover:border-primary/30 dark:hover:border-primary/50 transition-all">
                <div class="flex justify-between items-start z-10">
                  <div class="p-2 rounded-full bg-primary/10 text-primary dark:border dark:border-primary/20">
                    <span class="material-symbols-outlined text-[20px]">redeem</span>
                  </div>
                  <div class="text-right">
                    <p class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight">{{ $t('analyticsPage.avgValue') }}</p>
                    <p class="text-2xl font-bold text-primary">${{ getMetricValue('avgGiftValue') || '0' }}</p>
                    <p class="text-[10px] text-slate-400 dark:text-slate-500 font-medium mt-0.5">{{ $t('analyticsPage.hoverDesc.avgGiftValue') }}</p>
                  </div>
                </div>
                <div class="h-12 w-full mb-0 z-0 opacity-70 group-hover:opacity-100 transition-opacity mt-2">
                  <div id="chart-7" class="h-full w-full"></div>
                </div>
              </div>
              
              <!-- KPI卡片 8: 高营收场次 -->
              <!-- <div class="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm flex flex-col justify-between gap-5 relative overflow-hidden group hover:border-primary/30 dark:hover:border-primary/50 transition-all">
                <div class="kpi-card-hover-overlay">
                  <p class="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">高营收场次</p>
                  <div v-if="topRevenueSessions.length > 0" class="flex flex-col items-center gap-1">
                    <div v-for="session in topRevenueSessions.slice(0, 3)" :key="session.sessionId" class="flex items-center justify-between w-full">
                      <span class="text-[10px] text-slate-400 dark:text-slate-500 font-medium">{{ formatShortDate(session.startTime) }}:</span>
                      <span class="text-[10px] font-bold text-primary">{{ formatRevenue(session.revenue) }}</span>
                    </div>
                  </div>
                  <div v-else class="text-slate-400 dark:text-slate-500 text-xs font-medium">{{ $t('analyticsPage.noData') }}</div>
                </div>
                <div class="flex justify-between items-start z-10">
                  <div class="p-2 rounded-full bg-primary/10 text-primary dark:border dark:border-primary/20">
                    <span class="material-symbols-outlined text-[20px]">trending_up</span>
                  </div>
                  <div class="text-right">
                    <p class="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight">高营收场次</p>
                  </div>
                </div>
                <div class="h-12 w-full mb-0 z-0 opacity-70 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span class="text-slate-400 dark:text-slate-600 text-xs font-medium">高营收场次</span>
                </div>
              </div> -->
            </div>
            
            <!-- Agent对话模块 - 简化版本 -->
            <div class="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm flex flex-col">
              <!-- 对话历史显示区域 -->
              <div v-if="conversationHistory.length > 0" class="flex-1 mb-4">
                <div class="max-h-96 overflow-y-auto pr-2 space-y-4">
                  <div 
                    v-for="(item, index) in conversationHistory" 
                    :key="index"
                    class="space-y-3"
                  >
                    <!-- 用户问题 -->
                    <div class="flex justify-end">
                      <div class="max-w-[80%] bg-primary/10 border border-primary/20 rounded-lg p-3">
                        <p class="text-xs font-semibold text-primary mb-1">{{ $t('analyticsPage.you') }}</p>
                        <p class="text-slate-900 dark:text-white text-sm">{{ item.question }}</p>
                      </div>
                    </div>
                    
                    <!-- AI回答 -->
                    <div class="flex justify-start">
                      <div class="max-w-[80%] bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                        <p class="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">{{ $t('analyticsPage.aiAssistant') }}</p>
                        <simple-markdown-renderer :content="item.answer" class="text-slate-900 dark:text-white text-sm"></simple-markdown-renderer>
                        
                        <!-- 对话ID和相关信息 -->
                        <div v-if="item.conversationId" class="mt-2 pt-2 border-t border-slate-300/50 dark:border-slate-700/50">
                          <p class="text-xs text-slate-500 dark:text-slate-400">
                            对话ID: {{ item.conversationId }}
                            <span v-if="item.referencedSessionsCount > 0" class="ml-2">
                              引用场次: {{ item.referencedSessionsCount }}
                            </span>
                            <span class="ml-2 text-slate-400 dark:text-slate-500">
                              {{ formatConversationTime(item.timestamp) }}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- 当前回答显示区域（兼容旧模式） -->
              <div v-if="agentAnswer && conversationHistory.length === 0" class="mb-4 bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                <p class="text-slate-600 dark:text-slate-300 text-sm font-semibold mb-2">AI answer：</p>
                <simple-markdown-renderer :content="agentAnswer" class="text-slate-900 dark:text-white text-sm"></simple-markdown-renderer>
                
                <!-- 对话ID和相关信息 -->
                <div v-if="currentConversationId" class="mt-3 pt-3 border-t border-slate-300/50 dark:border-slate-700/50">
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    对话ID: {{ currentConversationId }}
                    <span v-if="referencedSessionsCount > 0" class="ml-2">
                      引用场次: {{ referencedSessionsCount }}
                    </span>
                  </p>
                </div>
              </div>
              
              <!-- 加载状态 -->
              <div v-if="isAskingAgent" class="mb-4 flex items-center justify-center">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span class="ml-2 text-slate-600 dark:text-slate-400 text-sm">{{ $t('analyticsPage.aiThinking') }}</span>
              </div>
              
              <!-- 错误信息 -->
              <div v-if="agentError" class="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg p-3">
                <p class="text-red-600 dark:text-red-400 text-sm">{{ agentError }}</p>
              </div>
              
              <!-- 输入框区域 -->
              <div class="relative">
                <input 
                  v-model="agentQuestion"
                  @keyup.enter="askAgentQuestion"
                  :placeholder="$t('analyticsPage.generateScriptFor')" 
                  type="text" 
                  class="w-full h-12 px-4 pr-28 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white rounded-lg placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary focus:bg-white dark:focus:bg-slate-800 transition-all"
                />
                <button 
                  @click="askAgentQuestion"
                  :disabled="isAskingAgent"
                  class="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center gap-2 h-9 px-4 bg-primary hover:bg-primary-hover text-white text-sm font-bold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span class="material-symbols-outlined" style="font-size: 18px;">auto_awesome</span>
                  <span>{{ isAskingAgent ? $t('common.loading') : $t('analyticsPage.generate') }}</span>
                </button>
              </div>
              
              <!-- 示例问题和清空对话按钮 -->
              <div class="mt-4 flex flex-col gap-3">
                <!-- 清空对话按钮 -->
                <div v-if="conversationHistory.length > 0" class="flex justify-end">
                  <button 
                    @click="clearAgentConversation"
                    class="px-3 py-1.5 text-xs bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <span class="material-symbols-outlined text-[14px]">delete</span>
                    {{ $t('analyticsPage.clearConversationHistory') }}
                  </button>
                </div>
                
                <!-- 示例问题 -->
                <div v-if="conversationHistory.length === 0 && !agentAnswer && !isAskingAgent">
                  <p class="text-slate-500 dark:text-slate-400 text-xs mb-2">{{ $t('analyticsPage.tryTheseQuestions') }}</p>
                  <div class="flex flex-wrap gap-2">
                    <button 
                      v-for="(example, index) in exampleQuestions" 
                      :key="index"
                      @click="useExampleQuestion(example)"
                      class="px-3 py-1.5 text-xs bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      {{ example }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 右侧图表区域 -->
          <div class="col-span-12 lg:col-span-7 flex flex-col gap-6">
            <!-- 最近高收入直播会话 -->
            <div class="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-5">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-slate-900 dark:text-white text-lg font-bold">{{ $t('analyticsPage.recentHighRevenueSessions') }}</h3>
              </div>
              
              <!-- 加载状态 -->
              <div v-if="isTopRevenueLoading" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div v-for="n in 3" :key="n" class="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                  <div class="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mb-2"></div>
                  <div class="h-8 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mb-3"></div>
                  <div class="h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
                </div>
              </div>
              
              <!-- 错误信息 -->
              <div v-if="topRevenueError" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg p-4 mb-4">
                <p class="text-red-600 dark:text-red-400 text-sm">{{ topRevenueError }}</p>
              </div>
              
              <!-- 数据展示 -->
              <div v-if="!isTopRevenueLoading && !topRevenueError" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div 
                  v-for="session in topRevenueSessions.slice(0, 3)" 
                  :key="session.sessionId"
                  @click="goToSessionAnalytics(session)"
                  class="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 hover:border-primary/30 dark:hover:border-primary/50 hover:bg-slate-100 dark:hover:bg-slate-800 hover:shadow-md hover:shadow-primary/5 transition-all group cursor-pointer relative overflow-hidden flex flex-col justify-between"
                >
                  <div class="absolute top-2 right-2 p-1 opacity-40 group-hover:opacity-100 transition-opacity bg-slate-200 dark:bg-slate-700 rounded-full">
                    <span class="material-symbols-outlined text-slate-500 dark:text-slate-400 group-hover:text-primary text-sm">arrow_outward</span>
                  </div>
                  <div>
                    <p class="text-xs text-slate-500 dark:text-slate-400 font-semibold mb-2">{{ formatSessionDate(session.startTime) }}</p>
                    <p class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">${{ formatRevenue(session.revenue) }}</p>
                  </div>
                  <div class="flex items-center gap-1.5 mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                    <div class="p-1 rounded-full bg-primary/10 text-primary border border-primary/20">
                      <span class="material-symbols-outlined text-[14px] font-bold block">trending_up</span>
                    </div>
                    <span class="text-xs font-bold text-slate-600 dark:text-slate-300">${{ formatRevenuePerMinute(session.revenuePerMinute) }}/min</span>
                  </div>
                </div>
                
                <!-- 如果没有数据，显示提示信息 -->
                <div v-if="topRevenueSessions.length === 0" class="col-span-3 text-center py-8">
                  <p class="text-slate-500 dark:text-slate-400">{{ $t('analyticsPage.noHighRevenueSessions') }}</p>
                </div>
              </div>
            </div>

            <!-- 前20名潜在赞助商 -->
            <div
              class="flex flex-col flex-1 bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm">
              <div class="flex justify-between items-center px-4 pb-3 pt-5 border-b border-slate-200 dark:border-border-dark">
                <h2 class="text-slate-900 dark:text-white text-[22px] font-bold leading-tight tracking-[-0.015em]">
                  {{ $t('analyticsPage.topPotentialPatrons') }}
                </h2>
                <div class="flex items-center gap-2">
                  <span v-if="isPotentialPatronsLoading" class="text-xs text-slate-500 dark:text-slate-400">{{ $t('common.loading') }}</span>
                  <button 
                    @click="fetchPotentialPatrons" 
                    :disabled="isPotentialPatronsLoading"
                    class="p-1.5 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="刷新数据"
                  >
                    <span class="material-symbols-outlined text-sm">refresh</span>
                  </button>
                </div>
              </div>
              
              <!-- 加载状态 -->
              <div v-if="isPotentialPatronsLoading" class="flex-1 overflow-y-auto p-8">
                <div class="flex flex-col items-center justify-center gap-4">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <p class="text-slate-500 dark:text-slate-400 text-sm">{{ $t('analyticsPage.loadingPotentialPatrons') }}</p>
                </div>
              </div>
              
              <!-- 错误信息 -->
              <div v-else-if="potentialPatronsError" class="flex-1 overflow-y-auto p-4">
                <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
                  <p class="text-red-600 dark:text-red-400 text-sm">{{ potentialPatronsError }}</p>
                  <button 
                    @click="fetchPotentialPatrons"
                    class="mt-2 px-3 py-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/40 rounded transition-colors"
                  >
                    重试
                  </button>
                </div>
              </div>
              
              <!-- 数据展示 -->
              <div v-else class="flex-1 overflow-y-auto">
                <ul class="divide-y divide-slate-200 dark:divide-slate-700">
                  <li 
                    v-for="(patron, index) in potentialPatrons" 
                    :key="patron.userInfoId || index"
                    class="p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <div class="flex items-center gap-3 md:gap-4 w-full md:w-auto md:flex-1">
                      <span class="text-lg font-bold text-slate-500 dark:text-slate-500 w-5 text-center">{{ index + 1 }}</span>
                      <div
                        class="bg-center bg-no-repeat aspect-square bg-cover rounded-full size-10 flex-shrink-0 border border-slate-300 dark:border-slate-600"
                        data-alt="User avatar"
                        :style="{ backgroundImage: 'url(https://lh3.googleusercontent.com/aida-public/AB6AXuBdk--CruPpsmZy2JFiGhupc1nsGZt7-5lcWar7hpwmHICCQTY90JGKo6lcsdQEvMxJ0N2HToVbqW8kni_LNcFp2V8ouTIZ55QQE3SSf1bWowPM8K4b_-YHqcF-PfbYIKjcWMjPOcTP-exNTQO0TszmF8veGXP2Z5loxMW8PLcA4yqHzStBM4H8Yxi1oUBJyGkUgYDSbEktLmr8kro41hW8jb6eEZhc9vET92YcgGUGOj9uk89bRpfjgIC4n20u88LJy-2ywXROFK7a)' }">
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-slate-900 dark:text-white font-semibold truncate">{{ patron.userName || $t('analyticsPage.defaultValues.unknownUser') }}</p>
                        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-400 mt-1">
                          <span>{{ $t('analyticsPage.session') }}: {{ patron.sessionId || $t('analyticsPage.defaultValues.noSessionId') }}</span>
                          <span class="hidden sm:inline">•</span>
                          <span>{{ $t('analyticsPage.stayDuration') }}: {{ formatEntryDuration(patron.entryDurationSeconds) }}</span>
                        </div>
                      </div>
                    </div>
                    <div
                      class="flex justify-between md:justify-end items-center md:items-end gap-2 md:gap-0 md:flex-col md:text-right mt-2 md:mt-0 md:w-32">
                      <p class="text-primary font-bold text-lg md:text-xl">{{ formatRewardProbability(patron.rewardProbability) }}</p>
                      <p class="text-slate-500 dark:text-slate-500 text-xs">{{ $t('analyticsPage.rewardProbability') }}</p>
                    </div>
                  </li>
                  
                  <!-- 如果没有数据 -->
                  <li v-if="potentialPatrons.length === 0" class="p-8 text-center">
                    <p class="text-slate-500 dark:text-slate-400">{{ $t('analyticsPage.noPotentialPatrons') }}</p>
                  </li>
                </ul>
                
                <!-- 分页信息 -->
                <!-- <div v-if="potentialPatronsTotalCount > 0" class="px-4 py-3 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400">
                  <div class="flex justify-between items-center">
                    <span>共 {{ potentialPatronsTotalCount }} 条记录</span>
                    <span>第 {{ potentialPatronsPageIndex }} 页 / 共 {{ Math.ceil(potentialPatronsTotalCount / potentialPatronsPageSize) }} 页</span>
                  </div>
                </div> -->
              </div>
            </div>
          </div>
        </div>

        <!-- 底部统计信息 -->
        <div
          class="mt-2 mb-8 w-full bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
          <div class="flex flex-col md:flex-row items-center gap-6 w-full md:w-auto">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-500 dark:text-slate-400">
                <span class="material-symbols-outlined">schedule</span>
              </div>
              <div class="flex flex-col">
                <span class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">{{ $t('analyticsPage.totalTracked') }}</span>
                <span class="text-lg font-bold text-slate-900 dark:text-white">{{ creatorDetail ? (creatorDetail.totalTrackingDurationText || formatTrackingDuration(creatorDetail.totalTrackingDuration)) : '0h' }}</span>
              </div>
            </div>
            <div class="h-8 w-px bg-slate-200 dark:bg-slate-700 hidden md:block"></div>
            <div class="flex items-center gap-3">
              <div class="p-2 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-500 dark:text-slate-400">
                <span class="material-symbols-outlined">videocam</span>
              </div>
              <div class="flex flex-col">
                <span class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">{{ $t('analyticsPage.totalSessionsTitle') }}</span>
                <span class="text-lg font-bold text-slate-900 dark:text-white">{{ creatorDetail ? creatorDetail.totalSessionCount : 0 }}</span>
              </div>
            </div>
          </div>
          <div>
            <button @click="showUnbindConfirm = true" :disabled="isUnbinding"
              class="px-4 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-slate-200 dark:border-slate-700 hover:border-red-200 dark:hover:border-red-800/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {{ isUnbinding ? $t('analyticsPage.unbinding') : $t('analyticsPage.unsubscribe') }}
            </button>
          </div>
        </div>
        
        <!-- 解绑确认弹框 -->
        <div v-if="showUnbindConfirm" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div class="bg-white dark:bg-surface-dark rounded-3xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
            <div class="px-8 py-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-white dark:bg-surface-dark z-10">
              <div>
                <h2 class="text-2xl font-bold text-primary">{{ $t('analyticsPage.confirmUnbind') }}</h2>
                <p class="text-slate-500 dark:text-slate-400 text-sm mt-1">{{ $t('analyticsPage.unbindDescription') }}</p>
              </div>
              <button @click="showUnbindConfirm = false" class="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white rounded-full cursor-pointer transition-colors">
                <span class="material-symbols-outlined text-2xl">close</span>
              </button>
            </div>
            
            <div class="p-8 overflow-y-auto bg-white dark:bg-surface-dark space-y-6">
              <!-- 警告图标 -->
              <div class="flex justify-center">
                <div class="p-4 bg-red-50 dark:bg-red-900/20 rounded-full">
                  <span class="material-symbols-outlined text-red-500 dark:text-red-400 text-4xl">warning</span>
                </div>
              </div>
              
              <!-- 确认信息 -->
              <div class="text-center">
                <h3 class="text-lg font-bold text-slate-900 dark:text-white mb-2">{{ $t('analyticsPage.confirmUnbindQuestion') }}</h3>
                <p class="text-slate-600 dark:text-slate-400 text-sm">
                  {{ $t('analyticsPage.unbindWarning') }}
                </p>
              </div>
              
              <!-- 当前直播间信息 -->
              <div v-if="creatorDetail" class="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                <div class="flex items-center gap-3">
                  <div class="size-12 rounded-xl bg-cover bg-center" :style="{ backgroundImage: `url('${creatorDetail.creatorAvatarUrl || currentStreamer.avatar}')` }"></div>
                  <div class="flex-1">
                    <p class="text-slate-900 dark:text-white font-semibold">{{ creatorDetail.creatorNickname || currentStreamer.name }}</p>
                    <p class="text-slate-500 dark:text-slate-400 text-sm">@{{ creatorDetail.socialHandle || currentStreamer.username }}</p>
                  </div>
                </div>
                <div class="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p class="text-slate-500 dark:text-slate-400">{{ $t('analyticsPage.trackingDuration') }}</p>
                    <p class="text-slate-900 dark:text-white font-semibold">{{ creatorDetail ? (creatorDetail.totalTrackingDurationText || formatTrackingDuration(creatorDetail.totalTrackingDuration)) : '0h' }}</p>
                  </div>
                  <div>
                    <p class="text-slate-500 dark:text-slate-400">{{ $t('analyticsPage.liveSessions') }}</p>
                    <p class="text-slate-900 dark:text-white font-semibold">{{ creatorDetail ? creatorDetail.totalSessionCount : 0 }}</p>
                  </div>
                </div>
              </div>
              
              <!-- 操作按钮 -->
              <div class="flex gap-3 pt-4">
                <button @click="showUnbindConfirm = false" class="flex-1 px-4 py-3 text-sm font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl transition-colors">
                  {{ $t('analyticsPage.cancel') }}
                </button>
                <button @click="confirmUnbind" :disabled="isUnbinding" class="flex-1 px-4 py-3 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 border border-red-500 hover:border-red-600 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  <span v-if="isUnbinding" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  <span>{{ isUnbinding ? $t('analyticsPage.unbinding') : $t('analyticsPage.confirmUnbindButton') }}</span>
                </button>
              </div>
              
              <!-- 额外提示 -->
              <div class="text-center text-xs text-slate-500 dark:text-slate-500 pt-2">
                <p>{{ $t('analyticsPage.unbindHint') }}</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { getApiUrl } from '@/config/apiConfig';
import authService from '@/services/authService';
import * as echarts from 'echarts';
import SimpleMarkdownRenderer from '@/components/SimpleMarkdownRenderer.vue';

export default {
  name: 'Analytics',
  components: {
    SimpleMarkdownRenderer
  },
  data() {
    return {
      currentStreamer: {
        name: '',
        username: '',
        avatar: 'https://lh3.googleusercontent.com/aida-public/AB6AXuBatDxiYqIXaoVN-hAsw4VToBlOzeahCW1vHFIWccsNso0DrTL64jeb0KhMgEd_g22rIySld5Ez3VucWeE2A4f2HmorMoULxEx3yOkHNEOLWo4FALtDexxv4pCsP7VFBOjWtx-yPDDjJ-Kj4b60aB7V_oIsOJ0hFPxRn4vd7O8VW9GO6hEMo1JIDp-EkMCPJgtekKo1cYqBi0Lb2u_AaAa2ir6b5Lkgz0p8ysYlYGWZSHwzr6RyWkRrNBLs_-3-bjK9_IQEcOAeOpQd'
      },
      isUnbinding: false,
      unbindMessage: '',
      creatorDetail: null,
      isLoading: false,
      errorMessage: '',
      
      // 解绑确认弹框
      showUnbindConfirm: false,

      // 时间筛选相关
      selectedPeriod: '60d', // 默认选择60天
      selectedMonth: null,
      isOverviewLoading: false,
      overviewError: '',
      
      // 月份选择相关
      showMonthPicker: false,
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),

      // 聚合查询数据
      overviewData: null,

      // 高营收场次数据
      topRevenueSessions: [],
      isTopRevenueLoading: false,
      topRevenueError: '',

      // 图表实例
      chartInstances: {},

      // 支持的指标列表
      metrics: [
        'sessionCount',      // 新开播场次
        'duration',          // 直播时长
        'avgOnlineUsers',    // 平均在线用户
        'interactionDensity', // 互动密度
        'revenue',           // 营收统计
        'rewardUsers',       // 打赏用户
        'avgGiftValue',      // 平均礼物价值
        'topRevenueSessions' // 高营收场次
      ],

      // Agent对话相关
      languageMode: 'same', // 'same' or 'different'
      fontSize: 16, // 默认字体大小
      minFontSize: 12, // 最小字体大小
      maxFontSize: 24, // 最大字体大小
      scriptPrompt: '', // 脚本生成输入
      agentResponse: {
        sameLanguage: '基于您的直播数据，建议优化互动策略...',
        differentLanguage: 'ライブデータに基づき、インタラクション戦略の最適化を提案します...',
        phoneticGuide: 'raibu dēta ni motodzuki, intarakushon senryaku no saitekika o teian shimasu...',
        translation: '基于您的直播数据，建议优化互动策略...'
      },

      // 新的AI对话接口相关
      agentQuestion: '', // 用户问题
      agentAnswer: '', // AI回答
      currentConversationId: null, // 当前对话ID
      referencedSessionsCount: 0, // 引用的场次数量
      isAskingAgent: false, // 是否正在提问
      agentError: '', // 错误信息
      conversationHistory: [], // 对话历史记录
      exampleQuestions: [], // 示例问题（从多语言配置中获取）

      // Top 20 Potential Patrons 相关
      potentialPatrons: [], // 潜在赞助者列表
      isPotentialPatronsLoading: false, // 加载状态
      potentialPatronsError: '', // 错误信息
      potentialPatronsTotalCount: 0, // 总数量
      potentialPatronsPageSize: 20, // 每页大小
      potentialPatronsPageIndex: 1 // 当前页码
    }
  },
  created() {
    this.fetchCreatorDetail();
    // 监听直播间切换事件
    this.$root.$on('streamer-changed', this.handleStreamerChanged);
    // 监听语言切换事件
    this.$root.$on('language-changed', this.handleLanguageChanged);
  },
  beforeDestroy() {
    // 移除事件监听
    this.$root.$off('streamer-changed', this.handleStreamerChanged);
    this.$root.$off('language-changed', this.handleLanguageChanged);
  },
  methods: {
    // 格式化粉丝数
    formatFollowerCount(count) {
      if (!count && count !== 0) return null;
      if (count >= 1000000) {
        return (count / 1000000).toFixed(1) + 'M';
      } else if (count >= 1000) {
        return (count / 1000).toFixed(1) + 'K';
      }
      return count.toString();
    },

    // 获取绑定状态文本
    getBindingStatusText(status) {
      const statusMap = {
        1: this.$t('analyticsPage.bindingStatus.bound'),
        0: this.$t('analyticsPage.bindingStatus.unbound'),
        2: this.$t('analyticsPage.bindingStatus.binding'),
        3: this.$t('analyticsPage.bindingStatus.failed')
      };
      return statusMap[status] || this.$t('analyticsPage.bindingStatus.unknown');
    },

    // 处理直播间切换事件
    handleStreamerChanged(streamer) {
      console.log('收到直播间切换事件:', streamer);
      // 重新获取直播间详情
      this.fetchCreatorDetail();
    },

    // 处理语言切换事件
    handleLanguageChanged() {
      console.log('收到语言切换事件，重新渲染图表');
      // 重新渲染图表以更新tooltip中的语言
      this.$nextTick(() => {
        if (this.overviewData) {
          this.renderCharts();
        }
      });
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return null;
      try {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      } catch (error) {
        console.error('日期格式化失败:', error);
        return dateString;
      }
    },

    async fetchCreatorDetail() {
      try {
        this.isLoading = true;
        this.errorMessage = '';

        // 获取token
        const katoken = authService.getToken();
        if (!katoken) {
          this.errorMessage = this.$t('analyticsPage.errorMessages.userNotLoggedIn');
          return;
        }

        // 获取直播间ID
        const bindingInfo = localStorage.getItem('tiktok-binding-info');
        const roomId = localStorage.getItem('tiktok-live-room-id');
        const currentStreamer = localStorage.getItem('currentStreamer');

        let liveRoomId = null;

        if (bindingInfo) {
          try {
            const binding = JSON.parse(bindingInfo);
            liveRoomId = binding.roomId || binding.bindingSource;
          } catch (error) {
            console.error('解析bindingInfo失败:', error);
          }
        } else if (roomId) {
          liveRoomId = roomId;
        } else if (currentStreamer) {
          try {
            const streamerData = JSON.parse(currentStreamer);
            liveRoomId = streamerData.roomId || streamerData.name || currentStreamer;
          } catch (error) {
            liveRoomId = currentStreamer;
          }
        }

        if (!liveRoomId) {
          this.errorMessage = this.$t('analyticsPage.errorMessages.noBoundLiveRoom');
          return;
        }

        // 构建请求参数
        const params = new URLSearchParams({
          liveRoomId: liveRoomId
        });

        // 调用获取绑定直播间详情接口
        const response = await fetch(`${getApiUrl('USER_CREATOR_BINDING_DETAIL')}?${params}`, {
          method: 'GET',
          headers: {
            'req_app_id': 'LivePlugin',
            'req_app_secret': 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy',
            'katoken': katoken
          }
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`接口请求失败: ${response.status}`);
        }

        const result = await response.json();
        console.log('获取绑定直播间详情接口响应:', result);

        if (result.success && result.data) {
          console.log('直播间详情数据:', result.data);
          this.creatorDetail = result.data;

          // 检查主播的绑定状态和验证状态
          // 只有已绑定(bindingStatus: 1)且已验证(verificationStatus: 1)的主播才能查看详情
          if (result.data.bindingStatus !== 1 || result.data.verificationStatus !== 1) {
            console.warn('主播绑定状态或验证状态不符合要求，无法查看详情:', {
              bindingStatus: result.data.bindingStatus,
              verificationStatus: result.data.verificationStatus
            });
            
            // 清除无效的缓存
            localStorage.removeItem('tiktok-live-room-id');
            localStorage.removeItem('currentStreamer');
            
            // 显示错误信息
            this.errorMessage = '主播绑定申请正在审核中，暂无权限查看详情';
            this.creatorDetail = null;
            return;
          }

          // 更新当前主播信息
          this.currentStreamer = {
            name: result.data.creatorNickname || '',
            username: result.data.socialHandle || '',
            avatar: result.data.creatorAvatarUrl || this.currentStreamer.avatar
          };
        } else {
          throw new Error(result.errMessage || '获取直播间详情失败');
        }
      } catch (error) {
        console.error('获取直播间详情失败:', error);
        this.errorMessage = `获取直播间详情失败: ${error.message}`;
      } finally {
        this.isLoading = false;
      }
    },

    // 显示解绑确认弹框
    showUnbindConfirmDialog() {
      this.showUnbindConfirm = true;
    },
    
    // 确认解绑
    async confirmUnbind() {
      try {
        this.isUnbinding = true;
        this.unbindMessage = '正在解绑主播...';
        
        // 关闭确认弹框
        this.showUnbindConfirm = false;

        // 获取当前绑定的直播间信息
        const bindingInfo = localStorage.getItem('tiktok-binding-info');
        const roomId = localStorage.getItem('tiktok-live-room-id');
        const currentStreamer = localStorage.getItem('currentStreamer');

        console.log('解绑参数:', { bindingInfo, roomId, currentStreamer });

        // 获取token
        const katoken = localStorage.getItem('katoken');
        if (!katoken) {
          throw new Error('用户未登录，请先登录');
        }

        // 解析直播间信息
        let liveRoomId = null;
        let platform = 'tiktok'; // 默认平台

        if (bindingInfo) {
          try {
            const binding = JSON.parse(bindingInfo);
            liveRoomId = binding.roomId || binding.bindingSource;
            console.log('从bindingInfo获取直播间ID:', liveRoomId);
          } catch (error) {
            console.error('解析bindingInfo失败:', error);
          }
        } else if (roomId) {
          liveRoomId = roomId;
          console.log('从roomId获取直播间ID:', liveRoomId);
        } else if (currentStreamer) {
          try {
            const streamerData = JSON.parse(currentStreamer);
            liveRoomId = streamerData.roomId || streamerData.name || currentStreamer;
            console.log('从currentStreamer解析获取直播间ID:', liveRoomId);
          } catch (error) {
            liveRoomId = currentStreamer;
            console.log('从currentStreamer直接获取直播间ID:', liveRoomId);
          }
        }

        if (!liveRoomId) {
          throw new Error('未找到绑定的直播间信息');
        }

        // 构建请求体
        const requestBody = {
          liveRoomId: liveRoomId,
          platform: platform,
          cancelReason: '解绑'
        };

        console.log('解绑请求体:', requestBody);

        // 调用解绑接口
        const response = await fetch(getApiUrl('USER_CREATOR_BINDING_CANCEL'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'req_app_id': 'LivePlugin',
            'req_app_secret': 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy',
            'katoken': katoken
          },
          body: JSON.stringify(requestBody)
        });

        console.log('解绑接口响应状态:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('解绑接口请求失败:', response.status, errorText);
          throw new Error(`解绑请求失败: ${response.status}`);
        }

        const result = await response.json();
        console.log('解绑接口响应数据:', result);

        if (result.success) {
          this.unbindMessage = '解绑成功！';

          // 清除本地存储的绑定信息
          localStorage.removeItem('tiktok-binding-info');
          localStorage.removeItem('tiktok-live-room-id');
          localStorage.removeItem('currentStreamer');

          // 清除其他相关缓存
          const keysToRemove = [
            'dashboard_activities',
            'dashboard_historical_comments',
            'dashboard_potential_patrons',
            'dashboard_last_comment_time',
            'dashboard_suggestions',
            'dashboard_current_suggestion_index',
            'dashboard_showing_single_suggestion',
            'dashboard_pending_batch_suggestion',
            'generatedScriptData',
            'currentSessionId',
            'selectedUser',
            'referencedComments'
          ];

          keysToRemove.forEach(key => {
            if (localStorage.getItem(key)) {
              localStorage.removeItem(key);
              console.log(`已清除缓存数据: ${key}`);
            }
          });

          // 更新当前主播信息
          this.currentStreamer = {
            name: '未绑定主播',
            username: '请绑定主播',
            avatar: ''
          };

          // 显示成功消息
          alert('解绑成功！页面将刷新以更新状态。');

          // 刷新页面
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          throw new Error(result.errMessage || '解绑失败');
        }
      } catch (error) {
        console.error('解绑失败:', error);
        this.unbindMessage = `解绑失败: ${error.message}`;
        alert(`解绑失败: ${error.message}`);
        } finally {
        this.isUnbinding = false;
      }
    },
    
    // 原始的解绑方法（保持兼容性）
    async unbindCreator() {
      // 直接调用确认解绑方法
      this.confirmUnbind();
    },

    // 时间筛选相关方法
    selectPeriod(period) {
      this.selectedPeriod = period;
      this.selectedMonth = null;
      this.fetchOverviewData();
    },

    selectMonth(month) {
      this.selectedMonth = month;
      this.selectedPeriod = null;
      this.fetchOverviewData();
    },

    // 获取时间范围
    getDateRange() {
      const now = new Date();
      let startTime, endTime;

      if (this.selectedPeriod) {
        const days = parseInt(this.selectedPeriod);
        endTime = new Date(now);
        startTime = new Date(now);
        startTime.setDate(startTime.getDate() - days);
      } else if (this.selectedMonth !== null) {
        // 使用选择的年份和月份
        const year = this.currentYear;
        const month = this.selectedMonth;
        startTime = new Date(year, month, 1);
        endTime = new Date(year, month + 1, 0);
      } else {
        // 默认最近30天
        endTime = new Date(now);
        startTime = new Date(now);
        startTime.setDate(startTime.getDate() - 30);
      }

      // 格式化日期为 yyyy-MM-dd HH:mm:ss
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      return {
        startTime: formatDate(startTime),
        endTime: formatDate(endTime)
      };
    },

    // 获取直播间ID
    getLiveRoomId() {
      const bindingInfo = localStorage.getItem('tiktok-binding-info');
      const roomId = localStorage.getItem('tiktok-live-room-id');
      const currentStreamer = localStorage.getItem('currentStreamer');

      let liveRoomId = null;

      if (bindingInfo) {
        try {
          const binding = JSON.parse(bindingInfo);
          liveRoomId = binding.roomId || binding.bindingSource;
        } catch (error) {
          console.error('解析bindingInfo失败:', error);
        }
      } else if (roomId) {
        liveRoomId = roomId;
      } else if (currentStreamer) {
        try {
          const streamerData = JSON.parse(currentStreamer);
          liveRoomId = streamerData.roomId || streamerData.name || currentStreamer;
        } catch (error) {
          liveRoomId = currentStreamer;
        }
      }

      return liveRoomId;
    },

    // 获取聚合查询数据
    async fetchOverviewData() {
      try {
        this.isOverviewLoading = true;
        this.overviewError = '';

        // 获取token
        const katoken = authService.getToken();
        if (!katoken) {
          this.overviewError = '用户未登录，请先登录';
          return;
        }

        // 获取直播间ID
        const liveRoomId = this.getLiveRoomId();
        if (!liveRoomId) {
          this.overviewError = '未找到绑定的直播间信息';
          return;
        }

        // 获取时间范围
        const dateRange = this.getDateRange();

        // 构建请求体
        const requestBody = {
          liveRoomId: liveRoomId,
          startTime: dateRange.startTime,
          endTime: dateRange.endTime,
          metrics: this.metrics // 查询所有指标
        };

        console.log('聚合查询请求体:', requestBody);

        // 调用聚合查询接口
        const response = await fetch(getApiUrl('LIVE_DATA_ANALYSIS_OVERVIEW'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'req_app_id': 'LivePlugin',
            'req_app_secret': 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy',
            'katoken': katoken
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`聚合查询接口请求失败: ${response.status}`);
        }

        const result = await response.json();
        console.log('聚合查询接口响应:', result);

        if (result.success && result.data) {
          this.overviewData = result.data;
          this.renderCharts();
        } else {
          throw new Error(result.errMessage || '聚合查询失败');
        }
      } catch (error) {
        console.error('聚合查询失败:', error);
        this.overviewError = `聚合查询失败: ${error.message}`;
      } finally {
        this.isOverviewLoading = false;
      }
    },

    // 获取高营收场次数据
    async fetchTopRevenueSessions() {
      try {
        this.isTopRevenueLoading = true;
        this.topRevenueError = '';

        // 获取token
        const katoken = authService.getToken();
        if (!katoken) {
          this.topRevenueError = '用户未登录，请先登录';
          return;
        }

        // 获取直播间ID
        const liveRoomId = this.getLiveRoomId();
        if (!liveRoomId) {
          this.topRevenueError = '未找到绑定的直播间信息';
          return;
        }

        // 获取最近一个月的时间范围
        const now = new Date();
        const startTime = new Date(now);
        startTime.setMonth(startTime.getMonth() - 1); // 最近一个月

        // 格式化日期为 yyyy-MM-dd HH:mm:ss
        const formatDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };

        // 构建请求体
        const requestBody = {
          liveRoomId: liveRoomId,
          startTime: formatDate(startTime),
          endTime: formatDate(now)
        };

        console.log('高营收场次请求体:', requestBody);

        // 调用高营收场次接口
        const response = await fetch(getApiUrl('LIVE_DATA_ANALYSIS_TOP_REVENUE_SESSIONS'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'req_app_id': 'LivePlugin',
            'req_app_secret': 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy',
            'katoken': katoken
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`高营收场次接口请求失败: ${response.status}`);
        }

        const result = await response.json();
        console.log('高营收场次接口响应:', result);

        if (result.success && result.data) {
          this.topRevenueSessions = result.data.sessions || [];
        } else {
          throw new Error(result.errMessage || '获取高营收场次失败');
        }
      } catch (error) {
        console.error('获取高营收场次失败:', error);
        this.topRevenueError = `获取高营收场次失败: ${error.message}`;
      } finally {
        this.isTopRevenueLoading = false;
      }
    },

    // 渲染图表
    renderCharts() {
      if (!this.overviewData) return;

      // 渲染8个图表
      this.metrics.forEach((metric, index) => {
        const chartId = `chart-${index + 1}`;
        this.renderChart(chartId, metric);
      });
    },

    // 渲染单个图表
    renderChart(chartId, metric) {
      const chartElement = document.getElementById(chartId);
      if (!chartElement) return;

      // 销毁之前的图表实例
      if (this.chartInstances[chartId]) {
        this.chartInstances[chartId].dispose();
      }

      // 创建新的图表实例
      const chart = echarts.init(chartElement);
      this.chartInstances[chartId] = chart;

      // 获取数据
      const metricData = this.overviewData[metric];
      if (!metricData || !metricData.dailyStats) {
        // 如果没有数据，显示空图表
        chart.setOption({
          title: {
            text: this.getMetricTitle(metric),
            left: 'center',
            textStyle: {
              fontSize: 12,
              color: '#666'
            }
          },
          graphic: {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: this.$t('analyticsPage.noData'),
              fontSize: 14,
              fill: '#999'
            }
          }
        });
        return;
      }

      // 准备图表数据
      const dates = metricData.dailyStats.map(item => item.statDate);
      let values = [];
      let valueFormatter = null;

      switch (metric) {
        case 'sessionCount':
          values = metricData.dailyStats.map(item => item.sessionCount);
          break;
        case 'duration':
          values = metricData.dailyStats.map(item => item.durationMinutes);
          valueFormatter = value => `${value.toFixed(1)} ${this.$t('analyticsPage.chartUnits.minutes')}`;
          break;
        case 'avgOnlineUsers':
          values = metricData.dailyStats.map(item => item.avgOnlineUsers);
          valueFormatter = value => `${value.toFixed(0)} ${this.$t('analyticsPage.chartUnits.viewers')}`;
          break;
        case 'interactionDensity':
          values = metricData.dailyStats.map(item => item.density);
          valueFormatter = value => `${value.toFixed(2)}`;
          break;
        case 'revenue':
          values = metricData.dailyStats.map(item => item.revenuePerMinute);
          valueFormatter = value => `${this.$t('analyticsPage.chartUnits.currency')}${value.toFixed(2)}`;
          break;
        case 'rewardUsers':
          values = metricData.dailyStats.map(item => item.rewardUserCount);
          valueFormatter = value => `${value} ${this.$t('analyticsPage.chartUnits.tippers')}`;
          break;
        case 'avgGiftValue':
          values = metricData.dailyStats.map(item => item.avgGiftValue);
          valueFormatter = value => `${this.$t('analyticsPage.chartUnits.currency')}${value.toFixed(2)}`;
          break;
        default:
          values = metricData.dailyStats.map(item => item.value || 0);
      }

      // 配置图表选项
      const option = {
        title: {
          show: false // 隐藏标题
        },
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const date = params[0].axisValue;
            const value = params[0].data;
            const formattedValue = valueFormatter ? valueFormatter(value) : value;
            return `${date}<br/>${this.getMetricTitle(metric)}: ${formattedValue}`;
          }
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: {
            show: false // 隐藏X轴标签
          },
          axisLine: {
            show: false // 隐藏X轴线
          },
          axisTick: {
            show: false // 隐藏X轴刻度
          },
          splitLine: {
            show: false // 隐藏网格线
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            show: false // 隐藏Y轴标签
          },
          axisLine: {
            show: false // 隐藏Y轴线
          },
          axisTick: {
            show: false // 隐藏Y轴刻度
          },
          splitLine: {
            show: false // 隐藏网格线
          }
        },
        series: [{
          data: values,
          type: 'line',
          smooth: true,
          lineStyle: {
            color: '#00C67E',
            width: 2
          },
          itemStyle: {
            color: '#00C67E'
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(0, 198, 126, 0.3)' },
              { offset: 1, color: 'rgba(0, 198, 126, 0.05)' }
            ])
          },
          symbol: 'none', // 隐藏数据点
          symbolSize: 0
        }],
        grid: {
          left: 0,
          right: 0,
          bottom: 0,
          top: 0,
          containLabel: false
        }
      };

      chart.setOption(option);

      // 响应窗口大小变化
      window.addEventListener('resize', () => {
        chart.resize();
      });
    },

    // 获取指标标题
    getMetricTitle(metric) {
      const titles = {
        'sessionCount': this.$t('analyticsPage.kpiCards.sessionCount'),
        'duration': this.$t('analyticsPage.kpiCards.duration'),
        'avgOnlineUsers': this.$t('analyticsPage.kpiCards.avgOnlineUsers'),
        'interactionDensity': this.$t('analyticsPage.kpiCards.interactionDensity'),
        'revenue': this.$t('analyticsPage.kpiCards.revenue'),
        'rewardUsers': this.$t('analyticsPage.kpiCards.rewardUsers'),
        'avgGiftValue': this.$t('analyticsPage.kpiCards.avgGiftValue'),
        'topRevenueSessions': this.$t('analyticsPage.kpiCards.topRevenueSessions')
      };
      return titles[metric] || metric;
    },

    // 获取指标值
    getMetricValue(metric) {
      if (!this.overviewData || !this.overviewData[metric]) {
        return null;
      }
      
      const metricData = this.overviewData[metric];
      
      // 根据实际接口返回的数据结构提取值
      switch (metric) {
        case 'sessionCount':
          return metricData.totalSessionCount || 0;
        case 'duration':
          return metricData.totalDurationMinutes || 0;
        case 'avgOnlineUsers':
          return metricData.overallAvgOnlineUsers || 0;
        case 'interactionDensity':
          return metricData.overallAvgDensity || 0;
        case 'revenue':
          // 总营收应该显示累计总收入，而不是每分钟平均收益
          // 如果接口返回了totalRevenue，使用它；否则使用overallAvgRevenuePerMinute作为后备
          return metricData.totalRevenue || metricData.overallAvgRevenuePerMinute || 0;
        case 'rewardUsers':
          return metricData.totalRewardUsers || 0;
        case 'avgGiftValue':
          return metricData.avgGiftValue || 0;
        case 'topRevenueSessions':
          // 返回高营收场次的数量
          return metricData.sessions ? metricData.sessions.length : 0;
        default:
          return 0;
      }
    },

    // 格式化时长（完整格式）
    formatDuration(minutes) {
      if (!minutes && minutes !== 0) return '0' + this.$t('analyticsPage.hourUnit');
      
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = Math.round(minutes % 60);
      
      if (hours === 0) {
        return `${remainingMinutes}${this.$t('analyticsPage.minuteUnit')}`;
      } else if (remainingMinutes === 0) {
        return `${hours}${this.$t('analyticsPage.hourUnit')}`;
      } else {
        return `${hours}${this.$t('analyticsPage.hourUnit')} ${remainingMinutes}${this.$t('analyticsPage.minuteUnit')}`;
      }
    },

    // 格式化时长（简短格式）
    formatDurationShort(minutes) {
      if (!minutes && minutes !== 0) return '0' + this.$t('analyticsPage.hourUnit');
      
      const hours = Math.floor(minutes / 60);
      return `${hours}${this.$t('analyticsPage.hourUnit')}`;
    },

    // 初始化图表
    initCharts() {
      this.$nextTick(() => {
        if (this.overviewData) {
          this.renderCharts();
        }
      });
    },

    // 月份选择器相关方法
    toggleMonthPicker() {
      this.showMonthPicker = !this.showMonthPicker;
    },

    getMonthButtonText() {
      if (this.selectedMonth !== null) {
        const monthKeys = [
          'analyticsPage.monthPicker.january',
          'analyticsPage.monthPicker.february',
          'analyticsPage.monthPicker.march',
          'analyticsPage.monthPicker.april',
          'analyticsPage.monthPicker.may',
          'analyticsPage.monthPicker.june',
          'analyticsPage.monthPicker.july',
          'analyticsPage.monthPicker.august',
          'analyticsPage.monthPicker.september',
          'analyticsPage.monthPicker.october',
          'analyticsPage.monthPicker.november',
          'analyticsPage.monthPicker.december'
        ];
        const monthName = this.$t(monthKeys[this.selectedMonth]);
        return `${this.currentYear}${this.$t('analyticsPage.year')}${monthName}`;
      }
      return this.$t('analyticsPage.monthPicker.month');
    },

    getMonthName(monthIndex) {
      const monthKeys = [
        'analyticsPage.monthPicker.january',
        'analyticsPage.monthPicker.february',
        'analyticsPage.monthPicker.march',
        'analyticsPage.monthPicker.april',
        'analyticsPage.monthPicker.may',
        'analyticsPage.monthPicker.june',
        'analyticsPage.monthPicker.july',
        'analyticsPage.monthPicker.august',
        'analyticsPage.monthPicker.september',
        'analyticsPage.monthPicker.october',
        'analyticsPage.monthPicker.november',
        'analyticsPage.monthPicker.december'
      ];
      return this.$t(monthKeys[monthIndex]);
    },

    isSelectedMonth(monthIndex) {
      return this.selectedMonth === monthIndex;
    },

    selectMonthFromPicker(monthIndex) {
      this.selectedMonth = monthIndex;
      this.selectedPeriod = null;
      this.showMonthPicker = false;
      this.fetchOverviewData();
    },

    prevYear() {
      this.currentYear--;
    },

    nextYear() {
      this.currentYear++;
    },

    selectCurrentMonth() {
      const now = new Date();
      this.currentYear = now.getFullYear();
      this.currentMonth = now.getMonth();
      this.selectedMonth = this.currentMonth;
      this.selectedPeriod = null;
      this.showMonthPicker = false;
      this.fetchOverviewData();
    },

    // 格式化高营收场次日期
    formatSessionDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear();
        return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      } catch (error) {
        console.error('日期格式化失败:', error);
        return dateString;
      }
    },

    // 格式化营收金额
    formatRevenue(revenue) {
      if (!revenue && revenue !== 0) return '0';
      if (revenue >= 1000000) {
        return (revenue / 1000000).toFixed(1) + 'M';
      } else if (revenue >= 1000) {
        return (revenue / 1000).toFixed(1) + 'K';
      }
      return revenue.toString();
    },

    // 格式化每分钟营收
    formatRevenuePerMinute(revenuePerMinute) {
      if (!revenuePerMinute && revenuePerMinute !== 0) return '0';
      return revenuePerMinute.toFixed(2);
    },

    // 格式化简短日期（MM-DD格式）
    formatShortDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return `${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      } catch (error) {
        console.error('日期格式化失败:', error);
        return dateString;
      }
    },

    // 格式化跟踪时长（秒转时分格式）
    formatTrackingDuration(seconds) {
      if (!seconds && seconds !== 0) return '0h';

      const totalMinutes = Math.floor(seconds / 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (hours === 0) {
        return `${minutes}m`;
      } else if (minutes === 0) {
        return `${hours}h`;
      } else {
        return `${hours}h ${minutes}m`;
      }
    },

    // 查看完整报告
    viewFullReport() {
      this.$router.push('/analytics-single');
    },

    // 跳转到场次分析页面
    goToSessionAnalytics(session) {
      console.log('跳转到场次分析页面:', session);
      
      // 保存选中的sessionId到localStorage，以便AnalyticsSingle页面使用
      if (session.sessionId) {
        localStorage.setItem('selectedSessionId', session.sessionId);
        console.log('已保存selectedSessionId到localStorage:', session.sessionId);
      }
      
      // 跳转到AnalyticsSingle页面
      this.$router.push('/analytics-single');
    },

    // Agent对话相关方法
    setLanguageMode(mode) {
      this.languageMode = mode;
      
      // 当切换到跨语言模式时，自动触发翻译
      if (mode === 'different') {
        console.log('切换到跨语言模式，自动触发翻译');
        this.$nextTick(() => {
          // 延迟执行以确保DOM已更新
          setTimeout(() => {
            this.autoTranslateOnModeChange();
          }, 100);
        });
      }
    },

    // 增加字体大小
    increaseFontSize() {
      if (this.fontSize < this.maxFontSize) {
        this.fontSize += 2;
      }
    },

    // 减小字体大小
    decreaseFontSize() {
      if (this.fontSize > this.minFontSize) {
        this.fontSize -= 2;
      }
    },

    // 模式切换时自动翻译
    async autoTranslateOnModeChange() {
      console.log('自动翻译模式切换，触发翻译');
      
      if (this.languageMode === 'different') {
        // 当切换到跨语言模式时，立即翻译当前显示的内容
        await this.translateCurrentContent();
      }
    },

    // 翻译当前显示的内容
    async translateCurrentContent() {
      try {
        console.log('=== 开始翻译当前显示的内容 ===');
        
        // 翻译分析建议
        if (this.agentResponse.sameLanguage && this.agentResponse.sameLanguage !== '基于您的直播数据，建议优化互动策略...') {
          console.log('翻译分析建议:', this.agentResponse.sameLanguage);
          const translation = await this.callTranslationAPI(
            this.agentResponse.sameLanguage,
            'zh', // 源语言：中文
            'ja'  // 目标语言：日文
          );
          
          if (translation) {
            this.agentResponse.differentLanguage = translation;
            console.log('✅ 分析建议翻译成功:', translation);
          } else {
            this.agentResponse.differentLanguage = '翻译失败';
            console.error('❌ 分析建议翻译失败');
          }
        } else {
          console.log('跳过分析建议翻译 - 内容为空或默认内容');
        }

        console.log('=== 当前内容翻译完成 ===');

      } catch (error) {
        console.error('❌ 翻译当前内容失败:', error);
      }
    },

    // 调用翻译API
    async callTranslationAPI(text, sourceLang, targetLang) {
      console.log('=== 开始调用翻译API ===');
      console.log('翻译文本:', text);
      console.log('源语言:', sourceLang);
      console.log('目标语言:', targetLang);
      
      if (!text || text.trim() === '') {
        console.warn('翻译文本为空');
        return '';
      }
      
      try {
        // 构建翻译请求 - 使用正确的请求体格式
        const requestBody = {
          sourceText: text,
          source: sourceLang,
          target: targetLang,
          fromProductDTO: {
            productType: "100106"
          },
          accessAppId: "WithIM_WEB",
          accessAppSecret: "Abut9y1Rv45pJCek"
        };
        
        console.log('发送翻译请求体:', requestBody);
        
        // 获取katoken用于认证
        const katoken = localStorage.getItem('katoken');
        console.log('使用的katoken:', katoken ? '已获取' : '未获取');
        
        // 构建完整的API URL
        const apiUrl = getApiUrl('UTIP_TRANS_TEXT');
        console.log('翻译API URL:', apiUrl);
        
        // 调用UTIP翻译API - 使用特殊的认证信息
        console.log('开始发送fetch请求...');
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'req_app_id': 'K_SSO',
            'req_app_secret': 'S4PEisfzlx71yNv2',
            'katoken': katoken || ''
          },
          body: JSON.stringify(requestBody)
        });
        
        console.log('翻译API响应状态:', response.status, response.statusText);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('翻译API请求失败:', response.status, errorText);
          throw new Error(`翻译API请求失败: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('翻译API响应数据:', data);
        
        if (data.success && data.data && data.data.targetText) {
          console.log('✅ 翻译成功:', data.data.targetText);
          return data.data.targetText;
        } else {
          console.error('❌ 翻译API返回格式错误:', data);
          // 如果API不可用，返回一个简单的模拟翻译
          return this.getMockTranslation(text, sourceLang, targetLang);
        }
      } catch (error) {
        console.error('❌ 调用翻译API失败:', error);
        console.error('错误详情:', error.message);
        // 如果API调用失败，返回一个模拟翻译
        return this.getMockTranslation(text, sourceLang, targetLang);
      } finally {
        console.log('=== 翻译API调用结束 ===');
      }
    },
    
    // 获取模拟翻译（当真实API不可用时使用）
    getMockTranslation(text, sourceLang, targetLang) {
      const languageNames = {
        'zh': '中文',
        'en': '英文', 
        'ja': '日文'
      };
      
      const sourceName = languageNames[sourceLang] || sourceLang;
      const targetName = languageNames[targetLang] || targetLang;
      
      return `[${sourceName}->${targetName}] ${text}`;
    },

    // 打开脚本生成器模态框
    goToScriptGenerator() {
      this.showScriptGeneratorModal = true;
    },

    // 关闭脚本生成器模态框
    closeScriptGeneratorModal() {
      this.showScriptGeneratorModal = false;
      this.generatedScript = '';
      this.scriptPrompt = '';
      this.isRecording = false;
      if (this.recognition) {
        this.recognition.stop();
      }
      this.isGenerating = false;
    },

    // 复制生成的脚本
    copyGeneratedScript() {
      if (!this.generatedScript) return;

      navigator.clipboard.writeText(this.generatedScript).then(() => {
        // 简单的成功反馈
        alert('脚本已复制到剪贴板！');
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = this.generatedScript;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          alert('脚本已复制到剪贴板！');
        } catch (err) {
          alert('复制脚本失败');
        }
        document.body.removeChild(textArea);
      });
    },

    // 开始语音录制
    startVoiceRecording() {
      if (this.isRecording) {
        this.stopVoiceRecording();
        return;
      }

      // 检查浏览器支持
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        alert('您的浏览器不支持语音输入。请使用Chrome、Edge或Safari浏览器。');
        return;
      }

      this.isRecording = true;

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();

      this.recognition.continuous = false;
      this.recognition.interimResults = false;
      this.recognition.lang = 'en-US';

      this.recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        this.scriptPrompt += (this.scriptPrompt ? ' ' : '') + transcript;
      };

      this.recognition.onerror = (event) => {
        console.error('语音识别错误:', event.error);
        alert('语音输入失败，请重试。');
        this.isRecording = false;
      };

      this.recognition.onend = () => {
        this.isRecording = false;
      };

      try {
        this.recognition.start();
      } catch (error) {
        console.error('启动语音识别失败:', error);
        this.isRecording = false;
      }
    },

    // 停止语音录制
    stopVoiceRecording() {
      if (this.recognition) {
        this.recognition.stop();
      }
      this.isRecording = false;
    },

    // 处理键盘事件
    handleKeyDown(event) {
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        this.generateScriptInModal();
      }
    },

    // 在模态框中生成脚本
    async generateScriptInModal() {
      if (!this.canGenerateInModal) return;

      console.log('开始生成脚本，当前scriptPrompt值:', this.scriptPrompt);

      // 先获取用户输入的值，因为closeScriptGeneratorModal会清空scriptPrompt
      const userPrompt = this.scriptPrompt;

      console.log('保存的用户输入值:', userPrompt);

      // 先关闭模态框
      this.closeScriptGeneratorModal();
      this.isGenerating = true;

      try {
        // 立即显示用户填写的内容到分析建议区域，即使API可能失败
        this.displayUserPromptInAgentResponse(userPrompt);

        // 调用SCRIPT_GENERATION_GENERATE_BY_DESCRIPTION接口（使用保存的用户输入）
        const response = await this.callScriptGenerationAPI(userPrompt);

        if (response.success && response.data) {
          // 处理新的API响应格式
          const scriptText = response.data.scripts && response.data.scripts.length > 0
            ? response.data.scripts[0]
            : '生成脚本失败，无法获取脚本内容';

          console.log('脚本生成成功:', scriptText);

          // 将生成的脚本显示在分析建议区域
          this.displayGeneratedScriptInAgentResponse(scriptText, userPrompt);

          // 保存数据到localStorage以供其他组件使用（可选）
          localStorage.setItem('generatedScriptData', JSON.stringify({
            prompt: userPrompt,
            script: scriptText
          }));
        } else {
          console.error('脚本生成失败，但用户输入已显示');
        }
      } catch (error) {
        console.error('生成脚本错误:', error);
        // 即使API调用失败，用户输入的内容也已经显示在分析建议区域
      } finally {
        this.isGenerating = false;
      }
    },

    // 立即显示用户填写的内容到分析建议区域
    displayUserPromptInAgentResponse(prompt) {
      try {
        console.log('立即显示用户填写的内容到分析建议区域:', prompt);

        // 更新分析建议区域，显示用户填写的内容
        this.agentResponse.sameLanguage = `正在生成脚本: ${prompt}`;
        this.agentResponse.differentLanguage = `正在生成脚本: ${prompt}`;

        console.log('✅ 用户填写的内容已立即显示在分析建议区域');

        // 如果当前是跨语言模式，立即翻译用户填写的内容
        if (this.languageMode === 'different') {
          console.log('当前是跨语言模式，立即翻译用户填写的内容');
          this.$nextTick(() => {
            setTimeout(() => {
              this.translateCurrentContent();
            }, 100);
          });
        }

      } catch (error) {
        console.error('显示用户填写的内容失败:', error);
      }
    },

    // 将生成的脚本显示在分析建议区域
    displayGeneratedScriptInAgentResponse(scriptText, prompt) {
      try {
        console.log('将生成的脚本显示在分析建议区域:', { scriptText, prompt });

        // 处理脚本内容
        const processedScript = this.extractScriptContent(scriptText);

        // 更新分析建议区域
        this.agentResponse.sameLanguage = processedScript.sameLanguage;
        this.agentResponse.differentLanguage = processedScript.differentLanguage;
        this.agentResponse.phoneticGuide = processedScript.phoneticGuide || '';

        console.log('成功将生成的脚本显示在分析建议区域');

        // 如果当前是跨语言模式，自动翻译生成的内容
        if (this.languageMode === 'different') {
          console.log('当前是跨语言模式，自动翻译脚本');
          this.$nextTick(() => {
            setTimeout(() => {
              this.translateCurrentContent();
            }, 500);
          });
        }

      } catch (error) {
        console.error('显示生成的脚本失败:', error);
      }
    },

    // 提取脚本内容
    extractScriptContent(scriptText) {
      if (!scriptText) {
        return {
          sameLanguage: '暂无脚本内容',
          differentLanguage: '暂无脚本内容',
          phoneticGuide: ''
        };
      }

      console.log('提取脚本内容，原始文本:', scriptText);

      // 简单处理：直接使用原始文本
      const processedScriptText = scriptText.replace(/\n/g, '<br>');

      return {
        sameLanguage: processedScriptText,
        differentLanguage: scriptText,
        phoneticGuide: ''
      };
    },

    // 调用脚本生成API
    async callScriptGenerationAPI(prompt) {
      try {
        console.log('调用脚本生成API, 提示词长度:', prompt ? prompt.length : 0);

        // 获取katoken用于认证
        const katoken = localStorage.getItem('katoken');
        if (!katoken) {
          console.error('未找到katoken，无法调用脚本生成API');
          throw new Error('未登录，无法生成脚本');
        }

        // 确保有当前房间ID和会话ID
        const liveRoomId = this.getLiveRoomId();
        if (!liveRoomId) {
          console.error('当前房间ID不存在，无法生成脚本');
          throw new Error('请先连接直播间再生成脚本');
        }

        // 获取当前会话ID（如果有）
        const currentSessionId = localStorage.getItem('currentSessionId');

        // 构建请求体 - 使用正确的参数格式
        const requestBody = {
          liveRoomId: liveRoomId,
          sessionId: currentSessionId ? parseInt(currentSessionId) : null,
          description: prompt // 脚本描述
        };

        console.log('构建的脚本生成请求体:', JSON.stringify(requestBody, null, 2));

        const apiUrl = getApiUrl('SCRIPT_GENERATION_GENERATE_BY_DESCRIPTION');
        console.log('脚本生成API URL:', apiUrl);

        // 发送请求
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'req_app_id': 'LivePlugin',
            'req_app_secret': 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy',
            'katoken': katoken
          },
          body: JSON.stringify(requestBody)
        });

        console.log('脚本生成API响应状态:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('脚本生成API请求失败:', response.status, errorText);
          throw new Error(`脚本生成API请求失败: ${response.status}`);
        }

        const data = await response.json();
        console.log('脚本生成API完整响应数据:', data);

        return data;
      } catch (error) {
        console.error('调用脚本生成API失败:', error);
        throw error;
      }
    },

    // 新的AI对话接口相关方法
    
    // 向AI助手提问
    async askAgentQuestion() {
      if (!this.agentQuestion.trim()) {
        this.agentError = '请输入问题';
        return;
      }

      try {
        this.isAskingAgent = true;
        this.agentError = '';
        this.agentAnswer = '';

        // 获取直播间ID
        const liveRoomId = this.getLiveRoomId();
        if (!liveRoomId) {
          this.agentError = '未找到绑定的直播间信息';
          this.isAskingAgent = false;
          return;
        }

        // 获取认证token
        const katoken = authService.getToken();
        if (!katoken) {
          this.agentError = '用户未登录，请先登录';
          this.isAskingAgent = false;
          return;
        }

        // 构建请求体
        const requestBody = {
          question: this.agentQuestion,
          liveRoomId: liveRoomId,
          conversationId: this.currentConversationId || null
        };

        console.log('调用直播间AI对话接口，请求体:', requestBody);

        // 调用接口
        const response = await fetch(getApiUrl('LIVE_ROOM_AGENT_QUERY'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'req_app_id': 'LivePlugin',
            'req_app_secret': 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy',
            'katoken': katoken
          },
          body: JSON.stringify(requestBody)
        });

        console.log('接口响应状态:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`接口请求失败: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        console.log('接口响应数据:', result);

        if (result.success && result.data) {
          const answer = result.data.answer;
          const conversationId = result.data.conversationId;
          const referencedSessionsCount = (result.data.referencedSessions && result.data.referencedSessions.length) || 0;
          
          // 保存到对话历史
          this.conversationHistory.push({
            question: this.agentQuestion,
            answer: answer,
            conversationId: conversationId,
            referencedSessionsCount: referencedSessionsCount,
            timestamp: new Date().toISOString()
          });
          
          // 更新当前对话信息
          this.agentAnswer = answer;
          this.currentConversationId = conversationId;
          this.referencedSessionsCount = referencedSessionsCount;
          
          // 清空问题输入框
          this.agentQuestion = '';
          
          console.log('AI回答成功，已保存到对话历史，对话ID:', conversationId);
          console.log('引用的场次数量:', referencedSessionsCount);
          console.log('当前对话历史长度:', this.conversationHistory.length);
        } else {
          throw new Error(result.errMessage || '接口返回失败');
        }
      } catch (error) {
        console.error('调用直播间AI对话接口失败:', error);
        this.agentError = `提问失败: ${error.message}`;
      } finally {
        this.isAskingAgent = false;
      }
    },

    // 使用示例问题
    useExampleQuestion(question) {
      this.agentQuestion = question;
      // 自动触发提问
      this.$nextTick(() => {
        this.askAgentQuestion();
      });
    },

    // 清空对话
    clearAgentConversation() {
      this.agentAnswer = '';
      this.currentConversationId = null;
      this.referencedSessionsCount = 0;
      this.agentError = '';
      this.conversationHistory = [];
    },
    
    // 格式化对话时间
    formatConversationTime(timestamp) {
      if (!timestamp) return '';
      try {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        
        if (diffMins < 1) {
          return '刚刚';
        } else if (diffMins < 60) {
          return `${diffMins}分钟前`;
        } else if (diffMins < 1440) { // 24小时
          const hours = Math.floor(diffMins / 60);
          return `${hours}小时前`;
        } else {
          const month = date.getMonth() + 1;
          const day = date.getDate();
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          return `${month}-${day} ${hours}:${minutes}`;
        }
      } catch (error) {
        console.error('格式化对话时间失败:', error);
        return '';
      }
    },

    // 获取潜在赞助者数据
    async fetchPotentialPatrons() {
      try {
        this.isPotentialPatronsLoading = true;
        this.potentialPatronsError = '';

        // 获取token
        const katoken = authService.getToken();
        if (!katoken) {
          this.potentialPatronsError = '用户未登录，请先登录';
          return;
        }

        // 获取直播间ID
        const liveRoomId = this.getLiveRoomId();
        if (!liveRoomId) {
          this.potentialPatronsError = '未找到绑定的直播间信息';
          return;
        }

        // 构建请求体
        const requestBody = {
          liveRoomId: liveRoomId,
          minDurationSeconds: 60, // 单场平均停留时长不低于1min
          maxGiftValue: 10, // 单场出手金额小于10
          days: 14, // 过去2周
          pageIndex: this.potentialPatronsPageIndex,
          pageSize: this.potentialPatronsPageSize
        };

        console.log('获取潜在赞助者请求体:', requestBody);

        // 调用潜在赞助者接口
        const response = await fetch(getApiUrl('USER_SESSION_STATS_POTENTIAL_SPONSORS'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'req_app_id': 'LivePlugin',
            'req_app_secret': 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy',
            'katoken': katoken
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`潜在赞助者接口请求失败: ${response.status}`);
        }

        const result = await response.json();
        console.log('潜在赞助者接口响应:', result);

        if (result.success && result.data) {
          this.potentialPatrons = result.data || [];
          this.potentialPatronsTotalCount = result.totalCount || 0;
          this.potentialPatronsPageSize = result.pageSize || 20;
          this.potentialPatronsPageIndex = result.pageIndex || 1;
        } else {
          throw new Error(result.errMessage || '获取潜在赞助者失败');
        }
      } catch (error) {
        console.error('获取潜在赞助者失败:', error);
        this.potentialPatronsError = `获取潜在赞助者失败: ${error.message}`;
      } finally {
        this.isPotentialPatronsLoading = false;
      }
    },

    // 格式化奖励概率为百分比
    formatRewardProbability(probability) {
      if (!probability && probability !== 0) return '0%';
      return `${(probability * 100).toFixed(1)}%`;
    },

    // 格式化停留时长
    formatEntryDuration(seconds) {
      if (!seconds && seconds !== 0) return '0s';
      if (seconds < 60) {
        return `${seconds}s`;
      } else if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
      } else {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${minutes}m`;
      }
    },

    // 格式化礼物价值
    formatGiftValue(value) {
      if (!value && value !== 0) return '0';
      return value.toString();
    },

    // 格式化消息数量
    formatMessageCount(count) {
      if (!count && count !== 0) return '0';
      return count.toString();
    },

    // 格式化时间（显示为相对时间）
    formatRelativeTime(timeString) {
      if (!timeString) return '';
      try {
        const date = new Date(timeString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        
        if (diffMins < 1) {
          return '刚刚';
        } else if (diffMins < 60) {
          return `${diffMins}分钟前`;
        } else if (diffMins < 1440) { // 24小时
          const hours = Math.floor(diffMins / 60);
          return `${hours}小时前`;
        } else {
          const month = date.getMonth() + 1;
          const day = date.getDate();
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          return `${month}-${day} ${hours}:${minutes}`;
        }
      } catch (error) {
        console.error('格式化相对时间失败:', error);
        return timeString;
      }
    },

    // 初始化示例问题
    initExampleQuestions() {
      // 从多语言配置中获取示例问题
      this.exampleQuestions = this.$t('analyticsPage.exampleQuestions') || [];
    }
  },

  mounted() {
    // 页面加载完成后获取聚合数据
    this.fetchOverviewData();
    // 获取高营收场次数据
    this.fetchTopRevenueSessions();
    // 获取潜在赞助者数据
    this.fetchPotentialPatrons();
    // 初始化示例问题
    this.initExampleQuestions();
  },

  updated() {
    // 组件更新后重新初始化图表
    this.initCharts();
  },

  beforeDestroy() {
    // 销毁所有图表实例
    Object.values(this.chartInstances).forEach(chart => {
      if (chart && chart.dispose) {
        chart.dispose();
      }
    });
    this.chartInstances = {};
  }
}
</script>

<style scoped>
.analytics-container {
  @apply w-full h-full;
  grid-column: 1 / -1;
  /* 跨越所有列 */
  margin-left: -1.5rem;
  /* 抵消父容器的padding */
  margin-right: -1.5rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
</style>